import { useMemo } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';

// project imports
import Search from './Search';
import Message from './Message';
import Profile from './Profile';
import Localization from './Localization';
import Notification from './Notification';
import FullScreen from './FullScreen';
import Customization from './Customization';
import MobileSection from './MobileSection';
import MegaMenuSection from './MegaMenuSection';
import TourButton from './TourButton';

import { useIntl } from 'react-intl';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import useOnlineStatus from 'hooks/useOnlineStatus';
import { MenuOrientation } from 'config';
import DrawerHeader from 'layout/Dashboard/Drawer/DrawerHeader';

// blinking animation for offline indicator
const blink = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.3; }
`;

// ==============================|| HEADER - CONTENT ||============================== //

export default function HeaderContent() {
  const { menuOrientation } = useConfig();
  const { isOnline } = useOnlineStatus();
  const { user } = useAuth();
  const intl = useIntl();
  const isTherapist = user?.role === 'therapist';

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const localization = useMemo(() => <Localization />, []);

  const megaMenu = useMemo(() => <MegaMenuSection />, []);

  return (
    <>
      {menuOrientation === MenuOrientation.HORIZONTAL && !downLG && <DrawerHeader open={true} />}
      {!downLG && <Search />}
      {/* Hidden: MegaMenu (Explore Components) */}
      {/* {!downLG && megaMenu} */}
      {!downLG && (
        <Box data-tour="language-switcher" sx={{ display: 'inline-flex' }}>
          {localization}
        </Box>
      )}
      {downLG && <Box sx={{ width: '100%', ml: 1 }} />}

      {/* Offline Indicator — only for therapist role (other roles don't use offline features) */}
      {!isOnline && isTherapist && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.5,
            borderRadius: 2,
            bgcolor: 'error.lighter',
            mr: 2
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: 'error.main',
              animation: `${blink} 1.5s ease-in-out infinite`
            }}
          />
          <Typography variant="body2" fontWeight={600} color="error.main">
            {intl.formatMessage({ id: 'header.offline' })}
          </Typography>
        </Box>
      )}

      {(isOnline || !isTherapist) && (
        <Box sx={{ display: 'flex', alignItems: 'center' }} data-tour="header-actions">
          <TourButton />
          <Notification />
          {/* Hidden: Message icon */}
          {/* <Message /> */}
          {!downLG && <FullScreen />}
          <Customization />
        </Box>
      )}
      {(isOnline || !isTherapist) && !downLG && <Profile />}
      {(isOnline || !isTherapist) && downLG && <MobileSection />}
    </>
  );
}
